import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";

import {
  selectIsHintDismissed,
  setHintDismissed,
} from "../../../app/slices/OnboardingSlice";
import { HintId, HintIdValue } from "./hintIds";

export { HintId };
export type { HintIdValue };

/**
 * A one-time inline tip, rendered in the surface it describes.
 *
 * This is where gestures belong — not in the tour. Nobody retains a right-click
 * tip delivered in their first minute, before they have anything to right-click;
 * the same tip shown on an empty interaction list, at the moment they are about
 * to add one, is actionable.
 *
 * Each hint carries its own seen-flag, so dismissing one never suppresses
 * another.
 */

const BODY_KEY: Record<HintIdValue, string> = {
  [HintId.RightClickAdd]: "onboarding.hint.right_click_add",
  [HintId.MultiSelect]: "onboarding.hint.multi_select",
  [HintId.FeedbackPaste]: "onboarding.hint.feedback_paste",
  [HintId.FileExplorerUpload]: "onboarding.hint.file_explorer_upload",
  [HintId.FileExplorerFolders]: "onboarding.hint.file_explorer_folders",
  [HintId.TranslateAllOverwrites]: "onboarding.hint.translate_all_overwrites",
};

export interface ContextualHintProps {
  hintId: HintIdValue;
  /** Optional context-specific copy for a shared hint surface. */
  bodyKey?: string;
  /** Extra condition — the hint shows only when its surface warrants it. */
  when?: boolean;
  /**
   * "chip" is the inline rounded tip. "banner" is a flush full-width strip
   * matching `AssetScopeNotice`, for hints that live in a dialog's notice
   * band and must align with it rather than float inside it.
   */
  variant?: "chip" | "banner";
  className?: string;
}

const VARIANT_CLASSES: Record<"chip" | "banner", string> = {
  chip: "rounded-5 px-3 py-2 text-[12.5px]",
  banner:
    "border-b border-creator-border px-[16px] py-[9px] text-[11.5px] leading-[17px]",
};

export default function ContextualHint({
  hintId,
  bodyKey,
  when = true,
  variant = "chip",
  className,
}: ContextualHintProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isDismissed = useSelector(selectIsHintDismissed(hintId));

  if (isDismissed || !when) return null;

  return (
    <div
      data-testid={`onboarding-hint-${hintId}`}
      className={
        "flex items-start gap-2 bg-creator-primary-washed text-creator-text " +
        VARIANT_CLASSES[variant] +
        " " +
        (className ?? "")
      }
    >
      <LightbulbOutlinedIcon
        aria-hidden="true"
        className="text-creator-text-key"
        sx={{ fontSize: 15, flex: "none", mt: "1px" }}
      />
      <span className="min-w-0">{t(bodyKey ?? BODY_KEY[hintId])}</span>
      <button
        type="button"
        onClick={() => dispatch(setHintDismissed(hintId))}
        aria-label={String(t("onboarding.hint.dismiss"))}
        data-testid={`onboarding-hint-${hintId}-dismiss`}
        className="ml-auto cursor-pointer border-0 bg-transparent p-0 text-creator-text-sub"
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </button>
    </div>
  );
}
